import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative bg-dusty-900">
        <img
          src="https://images.unsplash.com/photo-1780733066665-21bebd5eeab5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxjb25zdWx0aW5nJTIwb2ZmaWNlJTIwdGVhbSUyMG1lZXRpbmclMjBjb3Jwb3JhdGUlMjBidXNpbmVzcyUyMGRlc2t8ZW58MHx8fHwxNzg5MjIwNzY0fDA&ixlib=rb-4.1.0&q=85"
          alt="Safe Consulting"
          className="absolute inset-0 h-full w-full object-cover opacity-40"
        />
        <div className="relative h-full flex flex-col justify-end p-12 text-white">
          <div className="h-16 w-16 rounded-2xl bg-white flex items-center justify-center mb-4 shadow-lg">
            <img src="/logo-safe.png" alt="Safe Consulting srl" className="h-12 w-12 object-contain" />
          </div>
          <h2 className="font-display text-4xl font-bold leading-tight">Safe Consulting srl</h2>
          <p className="mt-3 text-dusty-100 max-w-md">
            Gestione aziende, servizi, partner e royalties. Calcolo automatico di IVA e utile netto su ogni vendita.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 bg-slate-50">
        <form onSubmit={submit} className="w-full max-w-sm animate-fade-up" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="h-9 w-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center overflow-hidden">
              <img src="/logo-safe.png" alt="Safe Consulting srl" className="h-7 w-7 object-contain" />
            </div>
            <span className="font-display font-bold text-slate-900">Safe Consulting srl</span>
          </div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Accedi al gestionale</h1>
          <p className="text-sm text-slate-500 mt-1 mb-8">Inserisci le tue credenziali per continuare.</p>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-slate-700">Email</Label>
              <Input
                id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                required placeholder="nome@safeconsulting.it" data-testid="login-email"
                className="mt-1.5 bg-white"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <Input
                id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                required placeholder="••••••••" data-testid="login-password"
                className="mt-1.5 bg-white"
              />
            </div>
          </div>

          {error && (
            <div className="mt-4 text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="login-error">
              {error}
            </div>
          )}

          <Button type="submit" disabled={loading} data-testid="login-submit"
            className="w-full mt-6 bg-dusty-600 hover:bg-dusty-700 text-white h-11">
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Accedi
          </Button>
        </form>
      </div>
    </div>
  );
}
